import { jsx } from "react/jsx-runtime";
import { Stack, InputBase as InputBase$1 } from "@mui/material";
import cn from "../../../external/classnames/index.js";
import { forwardRef, useState, useRef, useMemo, memo } from "react";
import { sizeStyles, fieldSizeMap } from "../../common/Common.js";
import { TimesButton } from "../../common/TimesButton.js";
import { inputContainerStyles } from "../../common/styles.js";
import { createEvent } from "../../../utils/createEvent.js";
import { spreadSx } from "../../../utils/spreadSx.js";
import { inheritDataTestId } from "../../../utils/dataTestId.js";
import { setRefs } from "../../../utils/setRefs.js";
import { useFieldHasContent } from "./useFieldHasContent.js";
import { PasswordEyeButton } from "./PasswordEyeButton.js";
const MUI_INPUT_BASE_CLASSNAME = "MuiInputBase-input", CLEAR_BUTTON_CLASSNAME = "input-clear-button", Input_ = forwardRef((props, ref) => {
  const {
    name,
    disabled,
    maxLength,
    type,
    error,
    className,
    startAdornment,
    endAdornment,
    endAdornmentPadding
  } = props, {
    size = "md",
    noClearHandler,
    onChange,
    value,
    autoComplete,
    containerSx,
    ...restProps
  } = props, [showPassword, setShowPassword] = useState(!1), { hasContent, handleChange } = useFieldHasContent({
    onChange,
    value: value ?? void 0
  }), internalRef = useRef(void 0), handleClear = () => {
    props.readOnly || (internalRef.current && (internalRef.current.value = ""), handleChange(createEvent({ value: "", name })));
  }, sx = useMemo(() => ({
    [`.${MUI_INPUT_BASE_CLASSNAME}`]: {
      ...sizeStyles.inputStyles[size],
      ...hasContent || type === "password" ? sizeStyles.inputFilledStyles[size] : void 0,
      ...styles.input
    },
    ...endAdornmentPadding ? { paddingInlineEnd: 12 } : {}
  }), [endAdornmentPadding, hasContent, size, type]), startAdornmentContent = useMemo(() => startAdornment ? /* @__PURE__ */ jsx(Stack, { className: "start-adornment-container", sx: styles.startAdornment, children: startAdornment }) : void 0, [startAdornment]), containerStyles = [
    styles.inputContainer,
    {
      height: fieldSizeMap[size]
    },
    startAdornment ? styles.containerStylesWithStartAdornment : {},
    styles.passwordIconStyles,
    ...spreadSx(containerSx)
  ], computedType = type === "password" ? showPassword ? "text" : "password" : type === "number" ? "text" : type, autoCompleteProp = autoComplete === !1 ? "new-password" : autoComplete, showClearHandler = hasContent && !noClearHandler && !props.readOnly, endAdornmentContent = endAdornment || (type === "password" ? /* @__PURE__ */ jsx(
    PasswordEyeButton,
    {
      "data-test-id": inheritDataTestId(props, "password-eye"),
      size,
      eyeOpen: showPassword,
      onClick: () => {
        setShowPassword(!showPassword);
      }
    }
  ) : showClearHandler ? /* @__PURE__ */ jsx(
    TimesButton,
    {
      "data-test-id": inheritDataTestId(props, "clear-button"),
      size,
      onClick: handleClear
    }
  ) : void 0);
  return /* @__PURE__ */ jsx(
    Stack,
    {
      sx: containerStyles,
      className: cn(
        className,
        disabled ? "field-disabled" : void 0,
        error ? "field-error" : void 0
      ),
      children: /* @__PURE__ */ jsx(
        InputBase$1,
        {
          onChange: handleChange,
          value,
          ...restProps,
          "data-test-id": inheritDataTestId(restProps, "input-base"),
          className: cn(props.focus && "Mui-focused", restProps.className),
          autoComplete: autoCompleteProp,
          startAdornment: startAdornmentContent,
          endAdornment: endAdornmentContent,
          inputRef: setRefs(internalRef, ref),
          inputProps: {
            type: computedType,
            autoComplete: autoCompleteProp,
            maxLength,
            "data-test-id": inheritDataTestId(props, "input"),
            ...props.inputProps
          },
          sx: [sx, ...spreadSx(props.sx)]
        }
      )
    }
  );
});
Input_.displayName = "DraftInputBase";
const InputBase = memo(Input_);
InputBase.displayName = "InputBase";
const cs = inputContainerStyles({
  inputBodySelector: ".MuiInputBase-root",
  inputFocusedSelector: ".Mui-focused",
  disabledSelector: ".field-disabled",
  errorSelector: ".field-error",
  inputSelector: `.${MUI_INPUT_BASE_CLASSNAME}`
}), styles = {
  inputContainer: (t) => ({
    ...cs(t),
    textUnderlineOffset: "off"
  }),
  input: {
    textOverflow: "ellipsis",
    "&[placeholder]": {
      textOverflow: "ellipsis"
    },
    "&::-webkit-input-placeholder": {
      fontSize: "Raleway,RalewayLocal,sans-serif",
      /* Chrome/Opera/Safari */
      textOverflow: "ellipsis"
    },
    "&::-moz-placeholder": {
      /* Firefox 19+ */
      textOverflow: "ellipsis"
    }
  },
  startAdornment: {
    height: "100%",
    width: 24,
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none"
  },
  passwordIconStyles: (t) => ({
    "&:hover": {
      ".pwd-icon svg path": {
        fill: t.palette.grey[500]
      }
    },
    "&>div:has(.Mui-focused) + div .pwd-icon svg path": {
      fill: `${t.palette.grey[700]} !important`
    },
    "&>div:has(.Mui-disabled) + div .pwd-icon svg path": {
      fill: `${t.palette.grey[200]} !important`
    }
  }),
  containerStylesWithStartAdornment: {
    ".MuiInputBase-input.MuiInputBase-inputAdornedStart": {
      paddingInlineStart: 0
    },
    ".MuiInputBase-root": {
      paddingInlineStart: 12,
      gap: 8
    }
  }
};
export {
  CLEAR_BUTTON_CLASSNAME,
  InputBase,
  MUI_INPUT_BASE_CLASSNAME
};
//# sourceMappingURL=InputBase.js.map
